import React, { useState, Suspense } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Bell, ExternalLink } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useLocation } from "@/location/api";
import { useEquipmentList } from "@/equipment/api";
import { EquipmentDetailData } from "@/equipment/equipment";
import { Route } from "@/routes/_authenticated/organization/$organizationId/_non_installer/locations/$locationId";
import { useSelectedOrganizationId, useOrganizationMinimal } from "@/organization/api";
import { HealthStatus } from "@/installation/garyStatus";
import { BellButton } from "@/components/snooze/BellButton";
import { EquipmentSnoozeSheetWrapper } from "@/components/snooze/EquipmentSnoozeSheetWrapper";
import { LocationSnoozeSheetWrapper } from "@/components/snooze/LocationSnoozeSheetWrapper";
import { useLocationSnooze, useDeleteLocationSnooze } from "@/hooks/useSnooze";

import {
  MobilePageHeader, StatusDot, CountCard, healthStatusToCopilot,
} from "@/components/copilot";
import type { CopilotStatus } from "@/components/copilot";

const EquipmentRow = ({
  equipment,
  locationId,
  onSnoozeClick,
}: {
  equipment: EquipmentDetailData;
  locationId: number;
  onSnoozeClick: (eq: EquipmentDetailData) => void;
}) => {
  const organizationId = useSelectedOrganizationId();
  const copilotStatus = healthStatusToCopilot(equipment.health_status);
  const isSnoozed = equipment.is_effectively_snoozed;

  return (
    <Link
      to="/organization/$organizationId/locations/$locationId/equipment/$equipmentId"
      params={{ organizationId, locationId, equipmentId: equipment.id }}
      className={`flex items-center gap-[var(--space-md)] p-[var(--space-md)] bg-white border-b border-copilot-neutral-border/50 hover:bg-copilot-surface-sunken transition-colors cursor-pointer ${isSnoozed ? "opacity-50" : ""}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-copilot-text-primary truncate">
          {equipment.name}
        </div>
        <div className="text-xs text-copilot-text-secondary mt-0.5">
          {equipment.sublocation_name}
          {equipment.equipment_model && (
            <> · {equipment.equipment_model.manufacturer?.name} {equipment.equipment_model.model}</>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusDot status={copilotStatus} />
        <BellButton
          isSnoozed={isSnoozed}
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSnoozeClick(equipment);
          }}
        />
        <ChevronRight size={18} className="text-copilot-text-tertiary" />
      </div>
    </Link>
  );
};

const EquipmentList = ({
  locationId,
  onSnoozeClick,
}: {
  locationId: number;
  onSnoozeClick: (eq: EquipmentDetailData) => void;
}) => {
  const { data: { results: equipmentList } } = useEquipmentList(locationId);

  const statusGroups: { status: HealthStatus; copilot: CopilotStatus; label: string }[] = [
    { status: HealthStatus.green, copilot: "healthy", label: "No Issues" },
    { status: HealthStatus.yellow, copilot: "warning", label: "Warning" },
    { status: HealthStatus.red, copilot: "critical", label: "Critical" },
    { status: HealthStatus.black, copilot: "neutral", label: "Unmonitored" },
  ];

  return (
    <div>
      {statusGroups.map(({ status, copilot, label }) => {
        const items = equipmentList.filter((eq) => eq.health_status === status);
        if (items.length === 0) return null;
        return (
          <div key={status}>
            {/* Group header */}
            <div className="flex items-center gap-2 py-2 mt-[var(--space-md)] first:mt-0">
              <StatusDot status={copilot} size="sm" />
              <span className="text-xs font-semibold text-copilot-text-secondary">
                {label} ({items.length})
              </span>
            </div>
            {/* Equipment list card */}
            <div className="bg-white border border-copilot-neutral-border rounded-[var(--card-radius)] overflow-hidden md:grid md:grid-cols-2 xl:grid-cols-3">
              {items.map((eq) => (
                <EquipmentRow
                  key={eq.id}
                  equipment={eq}
                  locationId={locationId}
                  onSnoozeClick={onSnoozeClick}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const LocationPage = () => {
  const { locationId } = Route.useParams();
  const organizationId = useSelectedOrganizationId();
  const { data: orgData } = useOrganizationMinimal(organizationId);
  const { data: location } = useLocation(locationId);
  const { data: { results: equipmentList } } = useEquipmentList(locationId);

  const [mapVisible, setMapVisible] = useState(false);
  const [snoozeEquipment, setSnoozeEquipment] = useState<EquipmentDetailData | null>(null);
  const [locationSnoozeOpen, setLocationSnoozeOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<CopilotStatus | null>(null);

  // Count equipment by status
  const countByStatus = {
    healthy: equipmentList.filter((eq) => eq.health_status === HealthStatus.green).length,
    warning: equipmentList.filter((eq) => eq.health_status === HealthStatus.yellow).length,
    critical: equipmentList.filter((eq) => eq.health_status === HealthStatus.red).length,
  };

  const address = [location.address_line_1, location.city, location.state, location.postal_code].filter(Boolean).join(", ");
  const mapsUrl = address ? `https://maps.google.com/?q=${encodeURIComponent(address)}` : null;

  return (
    <div className="min-h-screen bg-copilot-surface-sunken">
      {/* Header */}
      <MobilePageHeader title={location.name} breadcrumb={orgData.name}>
        <div className="flex flex-col gap-1 mt-2">
          <span className="text-sm text-copilot-text-secondary">{address}</span>
          {mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-copilot-brand inline-flex items-center gap-1 hover:underline"
            >
              Open in Maps <ExternalLink size={12} />
            </a>
          )}
          <button
            onClick={() => setMapVisible(!mapVisible)}
            className="text-xs font-medium text-copilot-brand mt-1 hover:underline text-left"
          >
            {mapVisible ? "Hide map" : "View on map"}
          </button>
          {mapVisible && (
            <div className="bg-copilot-surface-sunken border-2 border-dashed border-copilot-neutral-border rounded-[var(--card-radius)] h-44 flex items-center justify-center text-xs text-copilot-text-tertiary mt-2">
              Map placeholder
            </div>
          )}
        </div>
      </MobilePageHeader>

      {/* Content */}
      <div className="px-[var(--page-padding)]">
        <div className="py-[var(--section-gap)] xl:grid xl:grid-cols-[300px_1fr] xl:gap-[var(--space-lg)]">

          {/* Sidebar (stacked on mobile, sidebar on desktop) */}
          <aside className="mb-[var(--section-gap)] xl:mb-0 xl:sticky xl:top-[120px] xl:self-start">
            {/* Status count cards */}
            <div className="grid grid-cols-3 gap-[var(--space-sm)]">
              <CountCard
                count={countByStatus.healthy}
                label="No Issues"
                status="healthy"
                active={statusFilter === "healthy"}
                onClick={() => setStatusFilter(statusFilter === "healthy" ? null : "healthy")}
              />
              <CountCard
                count={countByStatus.warning}
                label="Warning"
                status="warning"
                active={statusFilter === "warning"}
                onClick={() => setStatusFilter(statusFilter === "warning" ? null : "warning")}
              />
              <CountCard
                count={countByStatus.critical}
                label="Critical"
                status="critical"
                active={statusFilter === "critical"}
                onClick={() => setStatusFilter(statusFilter === "critical" ? null : "critical")}
              />
            </div>

          </aside>

          {/* Equipment list */}
          <section>
            <div className="flex items-center justify-between mb-[var(--space-sm)]">
              <h2 className="text-[var(--heading-section-size)] font-bold">Equipment</h2>
            </div>
            <Suspense fallback={<Spinner />}>
              <EquipmentList
                locationId={locationId}
                onSnoozeClick={(eq) => setSnoozeEquipment(eq)}
              />
            </Suspense>
          </section>
        </div>
      </div>

      {/* Snooze sheets */}
      {snoozeEquipment && (
        <EquipmentSnoozeSheetWrapper
          equipmentId={snoozeEquipment.id}
          equipmentName={snoozeEquipment.name}
          isSnoozed={snoozeEquipment.is_effectively_snoozed}
          open={!!snoozeEquipment}
          onOpenChange={(open) => { if (!open) setSnoozeEquipment(null); }}
          locationId={locationId}
          locationSnoozed={location.is_snoozed}
        />
      )}
    </div>
  );
};

export default LocationPage;
